import { z } from 'zod';

export const AllergenSchema = z.enum([
  'glutine',
  'crostacei',
  'uova',
  'pesce',
  'arachidi',
  'soia',
  'latte',
  'frutta_guscio',
  'sedano',
  'senape',
  'sesamo',
  'solfiti',
  'lupini',
  'molluschi',
]);
export type Allergen = z.infer<typeof AllergenSchema>;

export const ALLERGEN_LABELS_IT: Record<Allergen, string> = {
  glutine: 'Glutine',
  crostacei: 'Crostacei',
  uova: 'Uova',
  pesce: 'Pesce',
  arachidi: 'Arachidi',
  soia: 'Soia',
  latte: 'Latte',
  frutta_guscio: 'Frutta a guscio',
  sedano: 'Sedano',
  senape: 'Senape',
  sesamo: 'Sesamo',
  solfiti: 'Anidride solforosa e solfiti',
  lupini: 'Lupini',
  molluschi: 'Molluschi',
};

// Allergeni "ovvi" suggeriti dalla categoria food. Servono come pre-set quando
// si crea/importa un Food: l'utente può sempre modificare. NON sostituiscono
// l'analisi puntuale: es. una "salsa" in categoria "condimenti" può contenere
// uova/senape/glutine, ma non lo sappiamo dalla categoria.
export const ALLERGENS_BY_CATEGORY: Record<string, Allergen[]> = {
  carne: [],
  pesce: ['pesce'],
  verdura: [],
  frutta: [],
  latticini: ['latte'],
  // "Cereali e farine" comprende anche riso, mais, quinoa e grano saraceno:
  // la categoria da sola non dimostra la presenza di glutine.
  cereali: [],
  pasta_pane: ['glutine'],
  salumi: [],
  condimenti: [],
  spezie: [],
  bevande: [],
  surgelati: [],
  conserve: [],
  dolci: ['glutine', 'latte', 'uova'],
  // Non-food: prodotti di pulizia/detergenza. Nessun allergene alimentare.
  detersivi: [],
  // Non-food: packaging e monouso (scatole, coppette, cannucce, coperchi…).
  packaging: [],
  altro: [],
};

// Suggerisce allergeni a partire da nome + categoria del food. Usato in fase
// di creazione/import per pre-popolare la lista. Riconosce alcuni keyword
// frequenti (es. "burrata", "stracciatella" → latticini = latte) e poi
// fallback su `ALLERGENS_BY_CATEGORY[category]`.
export function suggestAllergens(name: string, category: string | null | undefined): Allergen[] {
  const n = (name ?? '').toLowerCase();
  const set = new Set<Allergen>();
  // Keyword override: latticini espliciti.
  const hasDairyKeyword =
    /\b(stracciatella|burrata|mozzarella|ricotta|pecorino|parmigiano|grana|gorgonzola|panna|burro|yogurt|formaggio|caciocavallo|provolone|fontina|mascarpone|robiola)\b/.test(n);
  if (hasDairyKeyword) {
    set.add('latte');
  }
  // Le uova condividono la categoria food "latticini" (label "Latticini e uova"),
  // ma NON contengono latte. Se il nome indica un prodotto a base uova (senza
  // alcun keyword caseario), sopprimiamo il fallback di categoria che aggiungerebbe
  // erroneamente "latte". Cfr. T-139.
  const isEggOnly = /\b(uovo|uova|tuorlo|albume)\b/.test(n) && !hasDairyKeyword;
  if (/\b(uovo|uova|tuorlo|albume|maionese)\b/.test(n)) set.add('uova');
  const declaresGlutenFree = /\b(senza\s+glutine|s\/?\s*glut(?:ine)?)\b/.test(n);
  const glutenFreeGrain = /\b(riso|mais|granturco|polenta|quinoa|miglio|amaranto|grano\s+saraceno)\b/.test(n);
  const glutenGrain = /\b(frumento|orzo|segale|farro|avena|kamut|grano(?!\s+saraceno))\b/.test(n);
  const glutenFood = /\b(farina|pane|pasta|gnocch|biscott|tortin|brioche|pizza|fettuccin|tagliatell|spaghett|paccher|rigaton|pasta\s+all)\b/.test(n);
  if (!declaresGlutenFree && (glutenGrain || (glutenFood && !glutenFreeGrain))) set.add('glutine');
  if (/\b(salmone|tonno|merluzzo|baccal|orata|branzin|spigola|trigl|sgombro|nasello|pesce|alici|acciug)\b/.test(n)) set.add('pesce');
  if (/\b(gambero|scampo|aragost|granchi|astice)\b/.test(n)) set.add('crostacei');
  if (/\b(cozz|vongol|seppi|calamar|polp|ostric|moscard)\b/.test(n)) set.add('molluschi');
  if (/\b(arachid)\b/.test(n)) set.add('arachidi');
  if (/\b(noce|nocciol|mandorl|pistacc|anacard|noci\b)\b/.test(n)) set.add('frutta_guscio');
  if (/\b(soia|tofu|edamam)\b/.test(n)) set.add('soia');
  if (/\b(sesamo|tahin)\b/.test(n)) set.add('sesamo');
  if (/\b(senape)\b/.test(n)) set.add('senape');
  if (/\b(sedano)\b/.test(n)) set.add('sedano');
  if (/\b(lupin)\b/.test(n)) set.add('lupini');
  if (/\b(solfit|solfor|vino\b)\b/.test(n)) set.add('solfiti');
  // Fallback alla categoria (additivo: i keyword non escludono il cat default).
  const fromCat = category ? ALLERGENS_BY_CATEGORY[category] : undefined;
  for (const a of fromCat ?? []) {
    // Un prodotto a base uova nella categoria "latticini" non deve ereditare
    // l'allergene "latte" dal fallback di categoria (T-139).
    if (a === 'latte' && isEggOnly) continue;
    if (a === 'glutine' && declaresGlutenFree) continue;
    set.add(a);
  }
  return [...set];
}

// Categorie food: usate nella UI (select) e nei suggerimenti allergeni.
// Centralizzate qui per riusarle dal mobile, dal web e dalle edge functions.
export interface FoodCategoryDef { value: string; label: string; }
export const FOOD_CATEGORIES: FoodCategoryDef[] = [
  { value: 'carne', label: 'Carne' },
  { value: 'pesce', label: 'Pesce' },
  { value: 'verdura', label: 'Verdura' },
  { value: 'frutta', label: 'Frutta' },
  { value: 'latticini', label: 'Latticini e uova' },
  { value: 'cereali', label: 'Cereali e farine' },
  { value: 'pasta_pane', label: 'Pasta e pane' },
  { value: 'salumi', label: 'Salumi e affettati' },
  { value: 'condimenti', label: 'Condimenti e oli' },
  { value: 'spezie', label: 'Spezie' },
  { value: 'bevande', label: 'Bevande' },
  { value: 'surgelati', label: 'Surgelati' },
  { value: 'conserve', label: 'Conserve e scatolame' },
  { value: 'dolci', label: 'Dolci e dessert' },
  { value: 'detersivi', label: 'Detersivi' },
  { value: 'packaging', label: 'Packaging e monouso' },
  { value: 'altro', label: 'Altro' },
];

// Range temperatura target HACCP-grade per ciascun tipo di conservazione.
// Pre-compilato all'apertura del form FoodEditor; sovrascrivibile manualmente.
export interface StorageTempPreset { min: number; max: number; label: string; }
export const STORAGE_TYPE_TEMP: Record<string, StorageTempPreset> = {
  fresco:    { min: 0,   max: 4,   label: '0–4 °C' },
  surgelato: { min: -25, max: -18, label: '-25 / -18 °C' },
  secco:     { min: 10,  max: 25,  label: '10–25 °C' },
  bevanda:   { min: 4,   max: 16,  label: '4–16 °C' },
};

// T-419: nome da mostrare in ETICHETTA per un alimento nella lista ingredienti
// stampata. Usa `label_alias` (nome corto) se valorizzato, altrimenti il `name`.
// NON usare per DB/export ASL/trace-page: lì serve sempre il nome completo.
export function foodLabelName(
  food: { name: string; label_alias?: string | null } | null | undefined,
  fallback = '',
): string {
  if (!food) return fallback;
  const alias = food.label_alias?.trim();
  return alias && alias.length > 0 ? alias : food.name;
}

export const FoodSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  creation_source: z.literal('quick_label').nullable().optional(),
  name: z.string().min(1).max(200),
  // T-419: nome CORTO da mostrare in ETICHETTA quando questo alimento compare
  // nella lista ingredienti stampata (es. "Olio d'oliva EV" al posto del nome
  // lungo da carico DDT "OLIO EXTRAVERGINE DI OLIVA … 5L …"). Vuoto/null =
  // fallback al `name`. NON altera il nome nel DB, negli export ASL, né nella
  // trace-page (lì resta il nome completo per la tracciabilità).
  label_alias: z.string().max(60).nullable().optional(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  default_uom: z.string().max(10).default('kg'),
  storage_type: z.enum(['fresco', 'surgelato', 'secco', 'bevanda']).default('fresco'),
  target_temp_c_min: z.number().nullable(),
  target_temp_c_max: z.number().nullable(),
  shelf_life_days_closed: z.number().int().nullable(),
  shelf_life_days_opened: z.number().int().nullable(),
  // Ore di vita utile dopo scongelamento. HACCP: prodotti scongelati hanno
  // tipicamente shelf-life più breve dei "freschi" e dei "chiusi".
  shelf_life_hours_thawed: z.number().int().positive().nullable().default(null),
  allergens: z.array(AllergenSchema).default([]),
  barcode: z.string().nullable(),
  off_id: z.string().nullable(),
  photo_file_id: z.string().nullable(),
  // T-459: path bucket tech-sheets della scheda tecnica (PDF). null se assente.
  data_sheet_file_id: z.string().nullable().optional(),
  // T-221 (collettiva): se valorizzato, questo food è il "prodotto finito"
  // della ricetta indicata, creato automaticamente dal flusso etichette.
  // Al massimo uno attivo per ricetta per tenant (unique index parziale).
  recipe_id: z.string().uuid().nullable().optional(),
  // Lotto attivo: piano base mostra questi campi al posto della giacenza
  // per-lotto. Sovrascritti da ogni nuovo stock_in (vedi migrazione
  // 20260504090000). La storia completa vive negli events / fiscal_archive.
  current_batch_lot: z.string().nullable().optional(),
  current_expiration_date: z.string().nullable().optional(),
  current_document_id: z.string().uuid().nullable().optional(),
  current_supplier_id: z.string().uuid().nullable().optional(),
  current_lot_received_at: z.string().nullable().optional(),
  // T-407: storage path (bucket `documents`, prefix lot-labels/) della foto
  // etichetta del lotto corrente — scattata dal flusso "📷 Foto etichetta"
  // che estrae lotto+scadenza. Denorm da events.stock_in payload.label_photo_path
  // (stesso trigger di current_batch_lot). Prova verificabile del dato HACCP.
  current_lot_label_path: z.string().nullable().optional(),
  // T-301/T-481 (Food Cost): read model ricostruibile da acquisti e rettifiche
  // manuali append-only. Costo e UdM sono sempre una coppia dimensionale.
  last_unit_cost: z.number().nonnegative().nullable().optional(),
  // Food Cost storico (trigger su stock_in con prezzo): quando è stato osservato
  // l'ultimo costo + il costo precedente (per la variazione % "↑/↓").
  last_unit_cost_at: z.string().datetime().nullable().optional(),
  prev_unit_cost: z.number().nonnegative().nullable().optional(),
  last_unit_cost_uom: z.string().max(10).nullable().optional(),
  last_unit_cost_currency: z.string().length(3).nullable().optional(),
  last_unit_cost_event_id: z.string().uuid().nullable().optional(),
  last_unit_cost_document_date: z.string().date().nullable().optional(),
  last_unit_cost_source_kind: z
    .enum(['purchase', 'manual_adjustment'])
    .nullable()
    .optional(),
  // T-476: presa visione dell'UdM corretta nel Food Cost. Gli eventi storici
  // restano immutati; un nuovo carico successivo viene nuovamente controllato.
  cost_uom_reviewed_at: z.string().datetime().nullable().optional(),
  cost_uom_reviewed_uom: z.string().max(20).nullable().optional(),
  created_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
  version: z.number().int().nonnegative().default(0),
});
export type Food = z.infer<typeof FoodSchema>;
